import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  getFromSession,
  Logger,
  removeFromSession,
  setFromSession
} from 'wtb-utils';
import { Button } from 'wtb-components';
import { sendDetails, validateEmail } from 'api';
import {
  useAppContext,
  useBillingContext,
  useLocaleContext,
  useTransactionContext
} from 'context';
import { Warning } from 'components';
import { APP_STEPS, ERROR_TYPES, TRANSACTION_STATES } from 'constants.js';
import { cleanCustomerDataToBE, cleanCustomerDataToFE, getFieldErrors } from 'utils';
import { EXAMPLES } from 'utils/validation';
import styles from './ContinueToPaymentButton.module.css';

const ContinueToPaymentButton = (props) => {

  const history = useHistory();

  const {
    hasErrors,
    isValidating,
    validateAndGetFormData,
    setError
  } = props;

  const {
    transaction: { id },
    setActions,
    setCustomerData,
    setHasSentDetails,
    setPaymentPlans,
    setSelectedPaymentPlan,
    setEmailValidationData,
    transaction
  } = useTransactionContext();
  const { billing, setBilling } = useBillingContext();
  const { locale, translate } = useLocaleContext();
  const {
    setAppStep,
    setAppErrorType
  } = useAppContext();

  const [ isLoading, setIsLoading ] = useState(false);
  const [ hasTriedToSend, setHasTriedToSend ] = useState(false);

  const configureBilling = (data) => {
    const _billing = {
      ...billing,
      email: data.customer?.email || data.contact?.email,
      ...cleanCustomerDataToFE({ ...data.billing, language: locale })
    };

    setBilling(_billing);
  };

  useEffect(() => {
    if (!hasTriedToSend) return;

    if (!hasErrors && !isValidating) {
      setHasTriedToSend(false);
    }
  }, [ hasTriedToSend, hasErrors, isValidating, setHasTriedToSend ]);

  const runEmailValidation = (formData) => new Promise((resolve, reject) => {
    if (transaction.state === TRANSACTION_STATES.EXPIRED) {
      setAppErrorType(ERROR_TYPES.EXPIRED);
      setAppStep(APP_STEPS.ERROR);
      return;
    }

    const isEmailCheckedOnBE = getFromSession('isEmailCheckedOnBE') === 'true';

    const data = {
      email: formData?.email
    };

    if (isEmailCheckedOnBE) {
      resolve(true);
    } else {
      validateEmail(data).then(() => {
        setFromSession('isEmailCheckedOnBE', true);
        resolve(true);
      }).catch((error) => {
        setIsLoading(false);
        if (error && error?.body?.result?.verdict) {
          setEmailValidationData(error.body.result);
          history.push(`/${id}/email-validation`);
        } else {
          reject(error);
        }
      });
    }
  });

  const handleClick = async () => {
    setHasTriedToSend(true);

    if (isLoading) {
      return;
    }

    let formData = await validateAndGetFormData();

    if (formData.success) {
      formData = formData.data;
    } else {
      Logger.error('Form error');
      Logger.error(formData.errors);
      return;
    }

    setIsLoading(true);

    if (billing && billing.email !== formData?.email) {
      removeFromSession('isEmailCheckedOnBE');
    }

    setIsLoading(true);

    runEmailValidation(formData)
      .then(() => {
        const BEData = cleanCustomerDataToBE(formData);
        sendDetails(id, BEData)
          .then((response) => {
            if (response.actions.pay === 'FORBIDDEN') {
              throw new Error();
            }

            setActions(response.actions);
            setPaymentPlans(response.paymentPlans);

            if (response.selectedPlan) {
              setSelectedPaymentPlan(response.selectedPlan);
            }

            configureBilling(response);

            setCustomerData((prev) => cleanCustomerDataToFE({
              ...prev,
              ...response.contact,
              ...response.customer,
              language: locale
            }));

            setHasSentDetails(true);

            setAppStep(APP_STEPS.PAYMENT);
          })
          .catch((error) => {
            const errors = getFieldErrors(error);

            if (!errors) {
              setAppStep(APP_STEPS.ERROR);
            } else if (errors[0]) {
              const { name, message } = errors[0];

              let format = EXAMPLES[name?.toUpperCase()];
              format = format ? format[BEData?.address?.country?.toUpperCase()] : null;

              setError(name, translate(message, { format }));
            } else {
              Logger.error('Unhandled error:', error, errors);
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch((error) => {
        Logger.error(error);
      });
  };

  return (
    <div className={styles.root}>
      {hasTriedToSend && (hasErrors || isValidating) && (
        <Warning className={styles.warning} message={translate('errors.form.warning')} />
      )}

      <Button
        className={styles.button}
        aria-label={translate('customerData.continueToPayment')}
        isLoading={isLoading}
        onClick={handleClick}
        type="button"
        id="go-payment"
      >
        {translate('customerData.continueToPayment')}
      </Button>
    </div>
  );
};

export default ContinueToPaymentButton;
